import { useEffect, useState } from 'react';
import { useAuthStore } from '../../../core/services/AuthStore';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../shared/components/ui/card';
import { Badge } from '../../../shared/components/ui/badge';
import { 
  Users, 
  Dam, 
  TrendingUp, 
  CheckCircle2, 
  XCircle,
  Clock,
  MessageSquare
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Message {
  id: string;
  body: string;
  type: string;
  status: string;
  createdAt: string;
  recipientsCount: number;
  successCount: number;
  failedCount: number;
}

export const DashboardPage = () => {
  const { user, token } = useAuthStore();
  const [stats, setStats] = useState({
    totalDams: 0,
    totalContacts: 0,
    totalMessages: 0,
    pendingMessages: 0,
    successMessages: 0,
    failedMessages: 0,
  });
  const [recentMessages, setRecentMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch contacts
        const contactsResponse = await fetch(`${API_BASE_URL}/contacts`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        // Fetch messages
        const messagesResponse = await fetch(`${API_BASE_URL}/messages`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        let totalContacts = 0;
        let messages: Message[] = [];

        if (contactsResponse.ok) {
          const contactsData = await contactsResponse.json();
          totalContacts = Array.isArray(contactsData) ? contactsData.length : 0;
        }

        if (messagesResponse.ok) {
          messages = await messagesResponse.json();
        }

        // Calculate message statistics
        const totalMessages = messages.length;
        const pendingMessages = messages.filter((m) => m.status === 'PENDING').length;
        const sentMessages = messages.filter((m) => m.status === 'SENT');
        const failedMessages = messages.filter((m) => m.status === 'FAILED').length;

        // Total success count from all sent messages
        const successMessages = sentMessages.reduce((sum, m) => sum + (m.successCount || 0), 0);

        setStats({
          totalDams: user?.dams?.length || 0,
          totalContacts,
          totalMessages,
          pendingMessages,
          successMessages,
          failedMessages,
        });

        // Get 5 most recent messages
        setRecentMessages(messages.slice(0, 5));
      } catch (error) {
        console.error('Erro ao buscar dados do dashboard:', error);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDashboardData();
    }
  }, [user, token]);

  const statCards = [
    {
      title: 'Barragens',
      value: stats.totalDams,
      description: 'Total de barragens',
      icon: Dam,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
    },
    {
      title: 'Contatos',
      value: stats.totalContacts,
      description: 'Cadastrados no sistema',
      icon: Users,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
    },
    {
      title: 'Mensagens Enviadas',
      value: stats.successMessages,
      description: 'Com sucesso',
      icon: CheckCircle2,
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-100',
    },
    {
      title: 'Mensagens Pendentes',
      value: stats.pendingMessages,
      description: 'Aguardando envio',
      icon: Clock,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
  ];

  return (
    <div className="p-8 space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
        <p className="text-slate-600">
          Bem-vindo, <span className="font-medium">{user?.name || user?.email}</span>
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center items-center py-12">
          <div className="text-slate-600">Carregando dados...</div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {statCards.map((stat, index) => {
              const Icon = stat.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-slate-600">
                      {stat.title}
                    </CardTitle>
                    <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`w-5 h-5 ${stat.color}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold text-slate-900">{stat.value}</div>
                    <p className="text-xs text-slate-500 mt-1">{stat.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Activity Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Recent Messages */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" />
                  Mensagens Recentes
                </CardTitle>
                <CardDescription>Últimas mensagens enviadas</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentMessages.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      Nenhuma mensagem recente
                    </div>
                  ) : (
                    recentMessages.map((message) => (
                      <div
                        key={message.id}
                        className="flex items-start justify-between p-3 border border-slate-200 rounded-lg hover:border-blue-500 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900 line-clamp-1">
                            {message.body}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-xs text-slate-500">
                              {new Date(message.createdAt).toLocaleDateString('pt-BR')}
                            </span>
                            <Badge variant="outline" className="text-xs">
                              {message.type === 'SMS' ? 'SMS' : 'WhatsApp'}
                            </Badge>
                          </div>
                        </div>
                        <div className="ml-4">
                          {message.status === 'SENT' && (
                            <Badge variant="secondary" className="bg-green-100 text-green-700">
                              Enviado
                            </Badge>
                          )}
                          {message.status === 'PENDING' && (
                            <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                              Pendente
                            </Badge>
                          )}
                          {message.status === 'FAILED' && (
                            <Badge variant="destructive">
                              Falhou
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Quick Stats */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Estatísticas Rápidas
                </CardTitle>
                <CardDescription>Resumo do mês atual</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded">
                        <CheckCircle2 className="w-4 h-4 text-green-600" />
                      </div>
                      <span className="text-sm font-medium">Taxa de sucesso</span>
                    </div>
                    <Badge variant="secondary">
                      {stats.totalMessages > 0
                        ? `${((stats.successMessages / stats.totalMessages) * 100).toFixed(1)}%`
                        : '0%'}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-red-100 rounded">
                        <XCircle className="w-4 h-4 text-red-600" />
                      </div>
                      <span className="text-sm font-medium">Falhas</span>
                    </div>
                    <Badge variant="outline">{stats.failedMessages}</Badge>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-amber-100 rounded">
                        <Clock className="w-4 h-4 text-amber-600" />
                      </div>
                      <span className="text-sm font-medium">Pendentes</span>
                    </div>
                    <Badge variant="outline">{stats.pendingMessages}</Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* User Dams */}
          {user?.dams && user.dams.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Dam className="w-5 h-5" />
                  Minhas Barragens
                </CardTitle>
                <CardDescription>
                  Você tem acesso a {user.dams.length} barragem{user.dams.length !== 1 ? 's' : ''}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {user.dams.map((dam) => (
                    <div
                      key={dam.id}
                      className="p-4 border border-slate-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all cursor-pointer"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-slate-900">{dam.name}</h3>
                        <Badge variant="secondary" className="text-xs">
                          Ativo
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-600">
                        Clique para ver detalhes
                      </p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

